import { useCallback, useEffect, useState } from "react";
import { BrainCircuit, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { api, asList, formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/format";

function learnedProductLabel(item) {
    if (item.status === "ambiguous" && Array.isArray(item.conflicting_products)) {
        const conflicts = item.conflicting_products.map((product) => (
            [product.sku, product.name].filter(Boolean).join(" — ") || product.id
        )).filter(Boolean);
        if (conflicts.length) return `Conflict: ${conflicts.join(" / ")}`;
    }
    const product = item.product || {};
    const sku = item.product_sku || product.sku || "";
    const name = item.product_name || product.name || "";
    const cultivar = item.product_cultivar || product.cultivar || "";
    const readable = [name, cultivar ? `'${cultivar}'` : ""].filter(Boolean).join(" ");
    return [sku, readable].filter(Boolean).join(" — ") || item.product_id || "Onbekend product";
}

function sourceLabel(item) {
    return item.source_text || item.display_text || item.alias_key || item.key || "Geen brontekst beschikbaar";
}

export default function ScanLearning() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [deletingId, setDeletingId] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get("/scan-learning");
            setItems(asList(data, ["scan_learning", "aliases", "mappings"]));
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail ?? "Aangeleerde scankoppelingen konden niet worden geladen"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const forget = async (item) => {
        const label = learnedProductLabel(item);
        if (!window.confirm(`Koppeling met ${label} vergeten? Een volgende scan zal dit product opnieuw proberen te herkennen.`)) {
            return;
        }
        setDeletingId(item.id);
        try {
            await api.delete(`/scan-learning/${encodeURIComponent(item.id)}`);
            setItems((current) => current.filter((entry) => entry.id !== item.id));
            toast.success("Aangeleerde koppeling is vergeten.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail ?? "Koppeling vergeten is mislukt"));
        } finally {
            setDeletingId("");
        }
    };

    return (
        <div data-testid="scan-learning-page">
            <PageHeader
                kicker="AI-scanner"
                title="Aangeleerde koppelingen"
                subtitle="Bekijk welke productkeuzes de scanner door gecontroleerde bestellingen heeft geleerd. Deze gegevens gelden alleen binnen jouw kwekerij."
                testid="scan-learning-header"
                actions={(
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-white text-sm text-ink-700 hover:bg-ink-50 disabled:opacity-50"
                        data-testid="btn-refresh-scan-learning"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        Vernieuwen
                    </button>
                )}
            />

            <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-5 flex gap-3 text-sm text-sky-950">
                <BrainCircuit className="h-5 w-5 shrink-0 mt-0.5 text-sky-700" />
                <div>
                    <div className="font-semibold">De scanner onthoudt alleen bevestigde productkeuzes</div>
                    <p className="text-xs text-sky-800 mt-1">
                        Een koppeling verwijderen wist die herkenningsregel. Producten, bestellingen en berekeningen worden niet aangepast.
                    </p>
                </div>
            </div>

            {error && (
                <div className="bg-rose-50 border border-rose-300 text-rose-800 rounded-md p-3 mb-4 flex items-center justify-between gap-3" role="alert">
                    <span className="text-sm">{error}</span>
                    <button type="button" onClick={load} className="text-sm font-semibold underline">Opnieuw proberen</button>
                </div>
            )}

            <div className="bg-white border border-border rounded-lg overflow-hidden">
                {loading ? (
                    <div className="px-5 py-12 text-center text-sm text-ink-500">Koppelingen laden…</div>
                ) : items.length === 0 ? (
                    <div className="px-5 py-12 text-center" data-testid="scan-learning-empty">
                        <BrainCircuit className="h-8 w-8 mx-auto text-ink-300" />
                        <div className="mt-3 font-medium text-ink-800">Nog niets aangeleerd</div>
                        <p className="text-sm text-ink-500 mt-1">
                            Zodra iemand een gescande productregel controleert of corrigeert, verschijnt de koppeling hier.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="scan-learning-table">
                            <thead>
                                <tr className="bg-ink-50 border-b border-border">
                                    <th className="text-left px-4 py-3 data-label">Tekst uit bestelling</th>
                                    <th className="text-left px-4 py-3 data-label">Gekoppeld product</th>
                                    <th className="text-left px-4 py-3 data-label">Gebruikt</th>
                                    <th className="text-left px-4 py-3 data-label">Laatst gebruikt</th>
                                    <th className="w-12"><span className="sr-only">Acties</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item.id} className="border-b border-border/60 last:border-b-0" data-testid={`scan-learning-row-${item.id}`}>
                                        <td className="px-4 py-3 max-w-md">
                                            <div className="font-mono text-xs text-ink-700 break-words" title={sourceLabel(item)}>
                                                {sourceLabel(item)}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 font-medium text-ink-900">
                                            {learnedProductLabel(item)}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs text-ink-600">
                                            {Number(item.hit_count ?? item.use_count ?? 0)}×
                                        </td>
                                        <td className="px-4 py-3 text-xs text-ink-600 whitespace-nowrap">
                                            {formatDate(item.last_used_at || item.last_confirmed_at || item.updated_at || item.created_at, { withTime: true })}
                                        </td>
                                        <td className="px-3 py-3 text-right">
                                            <button
                                                type="button"
                                                onClick={() => forget(item)}
                                                disabled={deletingId === item.id}
                                                className="inline-flex items-center justify-center p-2 rounded-md text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                                                title="Deze koppeling vergeten"
                                                aria-label={`Koppeling met ${learnedProductLabel(item)} vergeten`}
                                                data-testid={`btn-forget-scan-learning-${item.id}`}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
